/**
 * Sidebar field progress for the facility-power family (#1330).
 * Totals follow visible step cards + Additional Information, not the full legacy step.
 */

import { shouldShowFacilityPowerAdditionalInformation } from './facilityPowerAdditionalInformation';
import {
  resolveFacilityPowerVisibleSections,
  shouldShowPowerDistributionSection,
} from './facilityPowerScopeSections';
import {
  filterSubsectionFieldsForFormView,
  type FormFieldVisibilityContext,
} from './formFieldVisibility';
import { countSubsectionFields } from './subsectionHeaderMetrics';
import {
  FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS,
  FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS,
  FACILITY_POWER_NOTES_SUBSECTION_FIELDS,
  FACILITY_POWER_SUBSECTION_FIELDS,
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
} from '../request/fields/requestFieldRegistry';

import type { SubsectionFieldDescriptor } from './subsectionHeaderMetrics';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';

export type FacilityPowerFamilyStepId =
  | 'facility-power'
  | 'large-ups'
  | 'power-distribution';

const LARGE_UPS_PER_INSTANCE_FIELD_SET = new Set<string>(
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS.map(field => field.key),
);
const POWER_DISTRIBUTION_PER_INSTANCE_FIELD_SET = new Set<string>(
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS.map(field => field.key),
);

function childConfigurationsFromValues<T>(
  values: Record<string, unknown>,
  key: string,
): T[] | undefined {
  const configs = values[key];
  if (!Array.isArray(configs) || configs.length === 0) {
    return undefined;
  }
  return configs as T[];
}

function filterProgressFields(
  fields: readonly SubsectionFieldDescriptor[],
  allowedKeys: ReadonlySet<string>,
): SubsectionFieldDescriptor[] {
  return fields.filter(field => allowedKeys.has(field.key));
}

function countFieldsAcrossRecords(
  fields: readonly SubsectionFieldDescriptor[],
  records: readonly Record<string, unknown>[],
): { filled: number; total: number } {
  if (fields.length === 0 || records.length === 0) {
    return { filled: 0, total: 0 };
  }
  const filled = records.reduce(
    (sum, record) => sum + countSubsectionFields(fields, record).filled,
    0,
  );
  return { filled, total: fields.length * records.length };
}

export function resolveFacilityPowerFamilyProgressFields(options: {
  stepId: FacilityPowerFamilyStepId;
  enabledStepIds: readonly string[];
  storedStepIds?: string[] | null;
  stage?: string | null;
}): readonly SubsectionFieldDescriptor[] {
  const fields: SubsectionFieldDescriptor[] = [];

  if (options.stepId === 'large-ups') {
    fields.push(...LARGE_UPS_EDITOR_SUBSECTION_FIELDS);
  } else if (options.stepId === 'power-distribution') {
    if (shouldShowPowerDistributionSection({ stage: options.stage })) {
      fields.push(...POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS);
    }
  } else {
    const visibleSections = resolveFacilityPowerVisibleSections({
      enabledStepIds: options.enabledStepIds,
      storedStepIds: options.storedStepIds,
    });

    if (visibleSections.has('readiness')) {
      fields.push(...FACILITY_POWER_SUBSECTION_FIELDS);
    }
    if (visibleSections.has('large-ups')) {
      fields.push(...FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS);
    }
    if (
      visibleSections.has('power-distribution') &&
      shouldShowPowerDistributionSection({ stage: options.stage })
    ) {
      fields.push(...FACILITY_POWER_DISTRIBUTION_SUBSECTION_FIELDS);
    }
  }

  if (
    shouldShowFacilityPowerAdditionalInformation({
      step: options.stepId,
      enabledStepIds: options.enabledStepIds,
      stage: options.stage,
    })
  ) {
    fields.push(...FACILITY_POWER_NOTES_SUBSECTION_FIELDS);
  }

  return fields;
}

export function getFacilityPowerFamilyStepFieldProgress(
  stepId: FacilityPowerFamilyStepId,
  values: Record<string, unknown>,
  options?: {
    enabledStepIds?: readonly string[];
    storedStepIds?: string[] | null;
    visibility?: FormFieldVisibilityContext;
  },
): { filled: number; total: number } | null {
  const enabledStepIds =
    options?.enabledStepIds ??
    (Array.isArray(values.enabledStepIds) ? values.enabledStepIds : []);

  const progressFields = filterSubsectionFieldsForFormView(
    resolveFacilityPowerFamilyProgressFields({
      stepId,
      enabledStepIds,
      storedStepIds: options?.storedStepIds,
      stage: String(values.sysRequestStage ?? values.stage ?? ''),
    }),
    options?.visibility,
  );

  if (progressFields.length === 0) {
    return null;
  }

  if (stepId === 'large-ups') {
    const configs = childConfigurationsFromValues<LargeUpsConfiguration>(
      values,
      'largeUpsConfigurations',
    );
    if (configs) {
      const perInstanceFields = filterProgressFields(
        progressFields,
        LARGE_UPS_PER_INSTANCE_FIELD_SET,
      );
      const noteFields = progressFields.filter(
        field => !LARGE_UPS_PER_INSTANCE_FIELD_SET.has(field.key),
      );
      const unitCount = countFieldsAcrossRecords(
        perInstanceFields,
        configs as unknown as Record<string, unknown>[],
      );
      const noteCount = countSubsectionFields(noteFields, values);
      return {
        filled: unitCount.filled + noteCount.filled,
        total: unitCount.total + noteCount.total,
      };
    }
  }

  if (stepId === 'power-distribution') {
    const configs =
      childConfigurationsFromValues<PowerDistributionConfiguration>(
        values,
        'powerDistributionConfigurations',
      );
    if (configs) {
      const perInstanceFields = filterProgressFields(
        progressFields,
        POWER_DISTRIBUTION_PER_INSTANCE_FIELD_SET,
      );
      const noteFields = progressFields.filter(
        field => !POWER_DISTRIBUTION_PER_INSTANCE_FIELD_SET.has(field.key),
      );
      const unitCount = countFieldsAcrossRecords(
        perInstanceFields,
        configs as unknown as Record<string, unknown>[],
      );
      const noteCount = countSubsectionFields(noteFields, values);
      return {
        filled: unitCount.filled + noteCount.filled,
        total: unitCount.total + noteCount.total,
      };
    }
  }

  return countSubsectionFields(progressFields, values);
}
